import React, { useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { buildLockupSvg, STACKED_COLOPHON_WIDTH } from "../admin/logoAssets";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PAGE_BG = [255, 255, 255]; // white, inside and outside the border
const OXBLOOD = [122, 46, 46]; // #7A2E2E

// Swap remote images for same-origin data URLs so html2canvas can render them.
async function inlineImages(el) {
  const imgs = Array.from(el.querySelectorAll("img"));
  await Promise.all(
    imgs.map(async (img) => {
      try {
        const proxied = `${BACKEND}/api/proxy-image?url=${encodeURIComponent(img.src)}`;
        const resp = await fetch(proxied);
        const blob = await resp.blob();
        const dataUrl = await new Promise((res, rej) => {
          const fr = new FileReader();
          fr.onload = () => res(fr.result);
          fr.onerror = rej;
          fr.readAsDataURL(blob);
        });
        img.src = dataUrl;
        img.removeAttribute("loading");
        await img.decode().catch(() => {});
      } catch (e) {
        /* leave original src on failure */
      }
    })
  );
}

// True when a horizontal row of the canvas is (visually) empty paper.
function makeRowChecker(canvas) {
  const W = canvas.width;
  const H = canvas.height;
  const data = canvas.getContext("2d").getImageData(0, 0, W, H).data;
  const x0 = Math.floor(W * 0.15);
  const x1 = Math.floor(W * 0.85);
  return (y) => {
    if (y < 0 || y >= H) return true;
    for (let x = x0; x < x1; x += 3) {
      const i = (y * W + x) * 4;
      if (data[i] < 236 || data[i + 1] < 234 || data[i + 2] < 228) return false;
    }
    return true;
  };
}

function buildA4Pdf(canvas, filename) {
  const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const pageMargin = 26; // page edge -> red border
  const contentPad = 20; // red border -> paper/content

  const borderW = pageW - pageMargin * 2;
  const borderH = pageH - pageMargin * 2;
  const innerX = pageMargin + contentPad;
  const innerY = pageMargin + contentPad;
  const innerW = pageW - innerX * 2;
  const innerHpt = pageH - innerY * 2;

  const ratio = innerW / canvas.width; // pt per source px
  const usableHpx = Math.floor(innerHpt / ratio);

  const isBlank = makeRowChecker(canvas);
  const H = canvas.height;
  const W = canvas.width;

  const drawChrome = () => {
    pdf.setFillColor(...PAGE_BG);
    pdf.rect(pageMargin, pageMargin, borderW, borderH, "F");
    pdf.setDrawColor(...OXBLOOD);
    pdf.setLineWidth(1.4);
    pdf.rect(pageMargin, pageMargin, borderW, borderH, "S");
  };

  let cursor = 0;
  let first = true;
  while (cursor < H - 2) {
    let target = Math.min(cursor + usableHpx, H);
    let breakY = target;
    if (target < H) {
      const floor = cursor + Math.floor(usableHpx * 0.35);
      for (let y = target; y > floor; y--) {
        if (isBlank(y)) {
          breakY = y;
          break;
        }
      }
    }
    const sliceH = breakY - cursor;
    if (sliceH <= 0) break;

    const tmp = document.createElement("canvas");
    tmp.width = W;
    tmp.height = sliceH;
    const tctx = tmp.getContext("2d");
    tctx.fillStyle = "#ffffff";
    tctx.fillRect(0, 0, W, sliceH);
    tctx.drawImage(canvas, 0, cursor, W, sliceH, 0, 0, W, sliceH);

    if (!first) pdf.addPage();
    first = false;
    drawChrome();
    pdf.addImage(tmp.toDataURL("image/jpeg", 0.95), "JPEG", innerX, innerY, innerW, sliceH * ratio);

    cursor = breakY;
    while (cursor < H && isBlank(cursor)) cursor++; // trim leading blank gap on next page
  }

  pdf.save(filename);
}

// Adds a "Written by Zukiswa Phoofolo" byline and, where the leaf has no
// press colophon yet, the Raven's Quill stacked lockup (emblem + wordmark +
// tagline) to the PDF clone.
function injectColophon(clone) {
  const hasColophon = !!clone.querySelector("svg");
  const wrap = document.createElement("div");
  wrap.style.cssText =
    "margin-top:48px;padding-top:26px;border-top:0.75px solid #A8A296;text-align:center;font-family:'Libre Baskerville',serif;";

  const byline = document.createElement("p");
  byline.textContent = "Written by Zukiswa Phoofolo";
  byline.style.cssText = "margin:0;font-style:italic;font-size:16px;color:#1C1A17;";
  wrap.appendChild(byline);

  if (!hasColophon) {
    const logo = document.createElement("div");
    logo.style.cssText = `width:${STACKED_COLOPHON_WIDTH}px;margin:20px auto 0;`;
    logo.innerHTML = buildLockupSvg("ink");
    const svg = logo.querySelector("svg");
    if (svg) {
      svg.style.display = "block";
      svg.style.width = "100%";
      svg.style.height = "auto";
    }
    wrap.appendChild(logo);
  }

  clone.appendChild(wrap);
}

export const SampleDownloadButton = ({ targetTestId, filename, label = "Download as PDF", testid }) => {
  const [busy, setBusy] = useState(false);

  const handleDownload = async () => {
    const el = document.querySelector(`[data-testid="${targetTestId}"]`);
    if (!el || busy) return;
    setBusy(true);

    const holder = document.createElement("div");
    holder.style.cssText = "position:fixed;left:-10000px;top:0;";
    const clone = el.cloneNode(true);
    clone.style.border = "none";
    clone.style.boxShadow = "none";
    clone.style.backgroundColor = "#ffffff";
    clone.style.width = "760px";
    injectColophon(clone);
    holder.appendChild(clone);
    document.body.appendChild(holder);

    try {
      await inlineImages(clone);
      await new Promise((r) => setTimeout(r, 120));
      const canvas = await html2canvas(clone, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
      });
      buildA4Pdf(canvas, filename);
    } finally {
      document.body.removeChild(holder);
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={busy}
      data-testid={testid}
      className="inline-flex items-center justify-center bg-transparent px-8 py-4 text-[11px] uppercase tracking-[0.28em] transition-colors duration-500 cursor-pointer border border-[#7A2E2E] text-[#7A2E2E] hover:bg-[#7A2E2E] hover:text-[#F4F1EA] disabled:opacity-60"
    >
      {busy ? "Preparing PDF..." : label}
    </button>
  );
};
